import dayjs from 'dayjs'

import { formatInViewerZone } from '../../../../ts-utils/date-utils'

/**
 * What the roster should show for an invitation.
 *
 * Acceptance outranks delivery: once someone is on the roster, an email that
 * bounced on an earlier attempt is history, not a problem to flag.
 *
 * `error` is reserved for the two states where doing nothing guarantees the
 * person never joins — a wrong address, and a spam complaint. Everything else
 * is informational, so a busy admin scanning the list sees only what needs them.
 */
export type InvitationStatusTone =
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'neutral'

export type InvitationStatusIcon =
  | 'check'
  | 'close'
  | 'stop'
  | 'info'
  | 'clock'
  | 'mail'

export interface InvitationStatusAppearance {
  label: string
  tooltip: string
  tone: InvitationStatusTone
  icon: InvitationStatusIcon
}

export interface InvitationStatusInput {
  isAccepted?: boolean
  isInvited?: boolean
  deliveryStatus?: string | null
  deliveryAt?: string | null
  /**
   * IANA zone for the delivery date in the tooltip. Mobile passes the device
   * zone; omitted, the viewer's zone is used.
   */
  timeZone?: string
}

const formatDeliveryDate = (
  value: string | null | undefined,
  timeZone?: string
): string | null => {
  if (!value) {
    return null
  }

  if (!timeZone) {
    const formatted = formatInViewerZone(value, 'MMM D, YYYY')
    return formatted || null
  }

  const date = dayjs(value)
  if (!date.isValid()) {
    return null
  }

  return date.tz(timeZone).format('MMM D, YYYY') || null
}

/**
 * Resolve the invitation tag for a roster player or team official.
 *
 * Returns null when there is no invitation, matching an empty Invite cell.
 * Older invitations pre-date delivery tracking, so a missing delivery status
 * falls back to the invited flag rather than showing nothing.
 */
export const resolveInvitationStatus = ({
  isAccepted,
  isInvited,
  deliveryStatus,
  deliveryAt,
  timeZone,
}: InvitationStatusInput): InvitationStatusAppearance | null => {
  const on = formatDeliveryDate(deliveryAt, timeZone)

  if (isAccepted) {
    return {
      label: 'Accepted',
      tooltip: 'This invitation has been accepted.',
      tone: 'success',
      icon: 'check',
    }
  }

  switch (deliveryStatus) {
    case 'bounced':
      return {
        label: 'Bounced',
        tooltip: on
          ? `The email couldn't be delivered on ${on}. Update the address and send again.`
          : "The email couldn't be delivered. Update the address and send again.",
        tone: 'error',
        icon: 'close',
      }

    case 'complained':
      return {
        label: 'Marked spam',
        tooltip: on
          ? `Reported as spam on ${on}. Please don't send this person more reminders.`
          : "Reported as spam. Please don't send this person more reminders.",
        tone: 'error',
        icon: 'close',
      }

    case 'unsubscribed':
      return {
        label: 'Opted out',
        tooltip: on
          ? `Asked to stop receiving invitation reminders on ${on}. Their invitation is still valid and they can accept it any time.`
          : 'Asked to stop receiving invitation reminders. Their invitation is still valid and they can accept it any time.',
        tone: 'warning',
        icon: 'stop',
      }

    case 'no_email':
      return {
        label: 'No email',
        tooltip:
          'There is no email address on file, so no invitation can be sent.',
        tone: 'neutral',
        icon: 'info',
      }

    case 'not_sent':
      return {
        label: 'Not sent',
        tooltip: 'An invitation exists but no email has gone out yet.',
        tone: 'neutral',
        icon: 'clock',
      }

    case 'delivered':
      return {
        label: 'Invited',
        tooltip: on ? `Delivered on ${on}.` : 'Delivered.',
        tone: 'info',
        icon: 'mail',
      }

    case 'sent':
      return {
        label: 'Invited',
        tooltip: on
          ? `Sent on ${on}. Delivery hasn't been confirmed yet.`
          : "Sent. Delivery hasn't been confirmed yet.",
        tone: 'info',
        icon: 'clock',
      }

    default:
      break
  }

  if (isInvited) {
    return {
      label: 'Invited',
      tooltip: 'An invitation has been sent.',
      tone: 'info',
      icon: 'mail',
    }
  }

  return null
}
